/* eslint-disable react/prop-types */
import clsx from 'clsx'
import type { EnumCell } from '../types'
import { cn, hashIdToColor } from '../../../../lib/utils'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../shadcn'
import { createRenderer } from './renderer'
import { Check } from 'lucide-react'
import { NOL } from './null'
import { ReactNode } from 'react'
import { ColoredBadge } from '../../colored-badge'

export const Enums = createRenderer<EnumCell>({
  View: ({ className, cell }) => {
    const { value, options, computed } = cell
    if (!value) {
      return <NOL />
    }
    const option = options.find((option) => option.value === value) ?? { value, label: value }

    return (
      <EnumBadge
        className={clsx(className, 'focus-visible:outline-none', { 'computed-cell': computed })}
        option={option}
      />
    )
  },
  Editor: ({ className, cell, row, onCellChange }) => {
    const comboboxOptions = cell.options.map((option) => ({
      value: option.value,
      label: typeof option.label === 'string' ? option.label : String(option.label || ''),
    }))

    const { value, options } = cell

    const option = options.find((option) => option.value === value) ?? (value ? { value, label: value } : null)

    const handleValueChange = (value: string) => {
      onCellChange?.(
        row,
        {
          type: 'enum',
          value,
          options: cell.options,
        },
        true
      )
    }

    return (
      <ComboCell
        open={true}
        options={comboboxOptions}
        value={cell.value ?? undefined}
        onValueChange={handleValueChange}
        placeholder="Search options..."
        emptyText="No options found."
        className={clsx(className, { 'computed-cell': cell.computed })}
        itemRenderer={(option) => {
          return (
            <>
              <Check className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')} />
              <EnumBadge className={clsx(className, 'focus-visible:outline-none')} option={option} />
            </>
          )
        }}
      >
        <div className="flex align-center pl-2 w-full">
          <EnumBadge
            className={clsx(className, 'focus-visible:outline-none', { 'computed-cell': cell.computed })}
            option={option ?? { value }}
          />
        </div>
      </ComboCell>
    )
  },
})

const EnumBadge = ({
  className,
  option,
}: {
  className: string
  option: { value: string | null; label?: ReactNode | string }
}) => {
  if (!option.value) {
    return <NOL />
  }
  const color = hashIdToColor(option.value)

  return (
    <ColoredBadge className={clsx(className, 'focus-visible:outline-none')} color={color}>
      {option.label ?? option.value}
    </ColoredBadge>
  )
}

interface ComboCellProps {
  options: { value: string; label?: ReactNode | string }[]
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  emptyText?: string
  className?: string
  disabled?: boolean
  itemRenderer?: (option: { value: string; label?: ReactNode | string }) => ReactNode
}

export function ComboCell({
  options,
  value,
  onValueChange,
  placeholder = 'Search...',
  emptyText = 'No options found.',
  open,
  children,
  onOpenChange,
  className,
  disabled = false,
  itemRenderer,
}: ComboCellProps) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger disabled={disabled} className={className} asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder={placeholder} className="border-none outline-none" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  className="text-foreground"
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    onValueChange?.(currentValue)
                  }}
                >
                  {itemRenderer ? (
                    itemRenderer(option)
                  ) : (
                    <>
                      <Check className={cn('mr-2 h-4 w-4', value === option.value ? 'opacity-100' : 'opacity-0')} />
                      {option.label}
                    </>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
